/**
 * Logique de l'écran « Comptes de TVA ». Les motifs (compte exact `4431` ou préfixe `4431*`) sont saisis
 * en texte libre séparé par des virgules ; la validation reprend les règles de `resoudreComptesTva`
 * avec des messages en français rattachés à la bonne ligne.
 */
import {
  COMPTES_TVA_PAR_DEFAUT,
  ComptesTvaInvalideError,
  ROLES_COMPTE_TVA,
  resoudreComptesTva,
  type RoleCompteTva,
  type TableComptesTva,
} from '@domain/fiscalite/ComptesTva';

export const LIBELLE_ROLE_TVA: Readonly<Record<RoleCompteTva, string>> = {
  collectee: 'TVA collectée',
  deductible_biens: 'TVA déductible sur biens et services',
  deductible_immo: 'TVA déductible sur immobilisations',
  due: 'TVA due / à payer',
  credit: 'Crédit de TVA',
};

export const MENTION_COMPTE_DE_LIQUIDATION =
  'Compte de liquidation : ignoré par la déclaration (ne fausse ni la collectée ni la déductible).';

export const AIDE_MOTIFS_TVA = '4431* = le compte 4431 et ses sous-comptes';

export const NOTE_COMPTE_4441 =
  'À savoir : par défaut, le compte 4441 est rangé en « TVA due ». Les écritures déjà passées sur 4441 ne sont donc plus lues comme de la TVA récupérable.';

export interface LigneEcranCompteTva {
  readonly role: RoleCompteTva;
  readonly libelle: string;
  readonly motifs: readonly string[];
  readonly mentionLiquidation: boolean;
  readonly modifie: boolean;
}

export function construireLignesComptesTva(table: TableComptesTva): LigneEcranCompteTva[] {
  return ROLES_COMPTE_TVA.map((role) => ({
    role,
    libelle: LIBELLE_ROLE_TVA[role],
    motifs: table[role],
    mentionLiquidation: role === 'due' || role === 'credit',
    modifie: JSON.stringify(table[role]) !== JSON.stringify(COMPTES_TVA_PAR_DEFAUT[role]),
  }));
}

export function motifsDepuisTexte(texte: string): string[] {
  const vus = new Set<string>();
  const out: string[] = [];
  for (const m of texte.split(/[\s,;]+/)) {
    const motif = m.trim();
    if (motif === '' || vus.has(motif)) continue;
    vus.add(motif);
    out.push(motif);
  }
  return out;
}

export function texteDepuisMotifs(motifs: readonly string[]): string {
  return motifs.join(', ');
}

export type SaisieComptesTva = Record<RoleCompteTva, readonly string[]>;

export type ResultatValidationComptesTva =
  | { ok: true; table: TableComptesTva }
  | { ok: false; erreurs: Partial<Record<RoleCompteTva, string>>; resume: string };

const FORMAT_MOTIF = /^[1-9]\d{2,7}\*?$/;

export function validerSaisieComptesTva(
  saisie: Readonly<Record<string, readonly string[]>>,
): ResultatValidationComptesTva {
  const erreurs: Partial<Record<RoleCompteTva, string>> = {};
  const vus = new Map<string, RoleCompteTva[]>();
  for (const role of ROLES_COMPTE_TVA) {
    const motifs = saisie[role] ?? [];
    if (motifs.length === 0) {
      erreurs[role] = 'Indiquez au moins un compte (exemple : 4431 ou 4431*).';
      continue;
    }
    const faux = motifs.find((m) => !FORMAT_MOTIF.test(m));
    if (faux !== undefined) {
      erreurs[role] = `« ${faux} » n'est pas un compte valable. Saisissez un compte (4431) ou un compte suivi d'une étoile (4431*) pour inclure ses sous-comptes.`;
      continue;
    }
    const pasEtat = motifs.find((m) => !m.startsWith('44'));
    if (pasEtat !== undefined) {
      erreurs[role] = `Le compte ${pasEtat} ne convient pas : un compte d'État (commençant par 44) est attendu.`;
      continue;
    }
    for (const m of motifs) vus.set(m, [...(vus.get(m) ?? []), role]);
  }
  for (const [motif, roles] of vus) {
    const distincts = [...new Set(roles)];
    if (distincts.length < 2) continue;
    for (const role of distincts) {
      const autres = distincts.filter((r) => r !== role).map((r) => `« ${LIBELLE_ROLE_TVA[r]} »`).join(', ');
      erreurs[role] = erreurs[role] ?? `Le compte ${motif} est déjà utilisé pour ${autres} : un compte ne peut servir qu'à un seul usage.`;
    }
  }
  if (Object.keys(erreurs).length > 0) return refus(erreurs);

  try {
    return { ok: true, table: resoudreComptesTva(saisie as Partial<Record<RoleCompteTva, readonly string[]>>) };
  } catch (err) {
    if (!(err instanceof ComptesTvaInvalideError)) throw err;
    let message: string = err.message;
    for (const role of ROLES_COMPTE_TVA) message = message.split(role).join(LIBELLE_ROLE_TVA[role]);
    return refus({ [ROLES_COMPTE_TVA[0]]: message });
  }
}

function refus(erreurs: Partial<Record<RoleCompteTva, string>>): ResultatValidationComptesTva {
  const n = Object.keys(erreurs).length;
  return {
    ok: false,
    erreurs,
    resume: n === 1 ? '1 ligne à corriger avant d’enregistrer.' : `${n} lignes à corriger avant d’enregistrer.`,
  };
}
